import { subYears } from 'date-fns';
import { CHART_COLORS, type ChartSeries, type TimePoint } from '@org/ui';
import { resolveRange, type AnalyticsSearch, type FilterInput } from './filters';

/** The window a comparison mode points at, same length as the current one. */
export function comparisonWindow(
  search: AnalyticsSearch,
  now: Date = new Date(),
): { from: Date; to: Date } | null {
  const range = resolveRange(search, now);
  if (search.compare === 'previous') {
    const length = range.to.getTime() - range.from.getTime();
    return { from: new Date(range.from.getTime() - length), to: new Date(range.from.getTime()) };
  }
  if (search.compare === 'lastYear') {
    return { from: subYears(range.from, 1), to: subYears(range.to, 1) };
  }
  return null;
}

/** Same filters, shifted window; null when nothing is being compared. */
export function comparisonInput(search: AnalyticsSearch, input: FilterInput, now: Date = new Date()): FilterInput | null {
  const window = comparisonWindow(search, now);
  if (!window) return null;
  return { ...input, from: window.from.toISOString(), to: window.to.toISOString() };
}

export const CMP_SUFFIX = '__cmp';

/**
 * Lays the comparison series over the current one, bucket by bucket (the
 * i-th bucket of each window lines up). Comparison series keep the hue of
 * the series they mirror and are drawn dashed.
 */
export function mergeComparison(
  data: TimePoint[],
  cmpData: TimePoint[] | null | undefined,
  series: ChartSeries[],
  cmpLabel: (label: string) => string,
): { data: TimePoint[]; series: ChartSeries[] } {
  if (!cmpData || cmpData.length === 0) return { data, series };
  const merged = data.map((point, i) => {
    const out: TimePoint = { ...point };
    const cmp = cmpData[i];
    for (const s of series) {
      const raw = cmp?.[s.key];
      out[`${s.key}${CMP_SUFFIX}`] = typeof raw === 'number' ? raw : Number(raw ?? 0) || 0;
    }
    return out;
  });
  const cmpSeries: ChartSeries[] = series.map((s, i) => ({
    key: `${s.key}${CMP_SUFFIX}`,
    label: cmpLabel(s.label),
    color: s.color ?? CHART_COLORS[Math.min(i, CHART_COLORS.length - 1)],
    dashed: true,
  }));
  return { data: merged, series: [...series, ...cmpSeries] };
}

/** Signed difference for a tile: percent change for counts, points for rates. */
export function tileDelta(
  current: number | null | undefined,
  compared: number | null | undefined,
  label: string,
  opts: { percentPoints?: boolean; upIsGood?: boolean } = {},
) {
  if (current === null || current === undefined || compared === null || compared === undefined) return undefined;
  if (opts.percentPoints) {
    return { value: Math.round((current - compared) * 10) / 10, label, format: 'number' as const, upIsGood: opts.upIsGood };
  }
  if (compared === 0) return undefined;
  return {
    value: Math.round(((current - compared) / compared) * 100),
    label,
    format: 'percent' as const,
    upIsGood: opts.upIsGood,
  };
}
